import React, { useRef, useEffect } from "react";
import style from "./ElementPositionListener.scss";
import scrollMonitor from "scrollmonitor";

/**

 elementWatcher.isInViewport - true if any part of the element is visible, false if not.
 elementWatcher.isFullyInViewport - true if the entire element is visible [1].
 elementWatcher.isAboveViewport - true if any part of the element is above the viewport.
 elementWatcher.isBelowViewport - true if any part of the element is below the viewport.
 elementWatcher.top - distance from the top of the document to the top of this watcher.
 elementWatcher.bottom - distance from the top of the document to the bottom of this watcher.
 elementWatcher.height - top - bottom.
 elementWatcher.watchItem - the element, number, or object that this watcher is watching.
 elementWatcher.offsets - an object that determines the offsets of this watcher. See "Offsets".

 */

export const ElementPositionListener = ({
    triggerTopOffset,
    triggerBottomOffset,
    onChange,
    onEnterViewport,
    onExitViewport,
    onFullyEnterViewport,
    onPartiallyExitViewport
}) => {
    const watcher = useRef();
    const elementRef = useRef();

    useEffect(() => {
        bindEvents();
        return () => {
            unbindEvents();
        };
    }, []);

    /**
     * BIND EVENTS
     * @private
     */
    const bindEvents = () => {
        // Bind container scroll events
        watcher.current =
            triggerTopOffset || triggerBottomOffset
                ? scrollMonitor.create(elementRef.current, { top: triggerTopOffset, bottom: triggerBottomOffset })
                : scrollMonitor.create(elementRef.current);

        // Elements position changes
        if (onChange) {
            watcher.current.stateChange(() => {
                onChange(watcher.current);
            });
        }

        // Elements enters viewport
        if (onEnterViewport) {
            watcher.current.enterViewport(() => {
                onEnterViewport(watcher.current);
            });
        }

        // Element has fully exited the viewport
        if (onExitViewport) {
            watcher.current.exitViewport(() => {
                onExitViewport(watcher.current);
            });
        }

        // Element is fully inside the viewport
        if (onFullyEnterViewport) {
            watcher.current.fullyEnterViewport(() => {
                onFullyEnterViewport(watcher.current);
            });
        }

        // Element has partially exited the viewport
        if (onPartiallyExitViewport) {
            watcher.current.partiallyExitViewport(() => {
                onPartiallyExitViewport(watcher.current);
            });
        }
    };

    const unbindEvents = () => {
        if (watcher.current) {
            watcher.current.destroy();
        }
    };

    return <div ref={elementRef} className={style.base} />;
};

ElementPositionListener.defaultProps = {
    active: true,
    triggerTopOffset: 0,
    triggerBottomOffset: 0
};
